/**
 * Direct-to-storage upload helper for heavy documents (PDFs, statements).
 *
 * Flow: POST /api/uploads/pdf/request-url (JSON metadata only — the server
 * generates the object key and an uploadId) → PUT the raw bytes straight to
 * the presigned storage URL → the caller then hits the pipeline-specific
 * finalize endpoint with { uploadId }. Express never sees the file bytes.
 */

export type DirectUploadDocumentType =
  | "floorplan"
  | "bank_statement"
  | "tax_return"
  | "financial_statement"
  | "dealer_document"
  | "onboarding"
  | "inventory_pdf";

export interface DirectUploadResult {
  uploadId: string;
}

export class DirectUploadRequestError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "DirectUploadRequestError";
  }
}

/**
 * Presign + PUT. Throws DirectUploadRequestError (server rejected the presign,
 * with server message) or Error ("upload_failed") when the storage PUT fails.
 */
export async function uploadFileDirect(
  file: File,
  documentType: DirectUploadDocumentType,
  onProgress?: (fraction: number) => void,
): Promise<DirectUploadResult> {
  const contentType = file.type || (file.name.toLowerCase().endsWith(".pdf") ? "application/pdf" : "application/octet-stream");
  const presignRes = await fetch("/api/uploads/pdf/request-url", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ name: file.name, size: file.size, contentType, documentType }),
  });
  if (!presignRes.ok) {
    const data = await presignRes.json().catch(() => ({} as any));
    throw new DirectUploadRequestError(presignRes.status, data.message || data.error || "Could not start the upload. Please try again.");
  }
  const { uploadId, uploadURL } = await presignRes.json();

  // XMLHttpRequest for byte-level progress (fetch has no upload progress).
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadURL);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error("upload_failed"));
    };
    xhr.onerror = () => reject(new Error("upload_failed"));
    xhr.ontimeout = () => reject(new Error("upload_failed"));
    xhr.send(file);
  });

  return { uploadId };
}

/** Classify an already-uploaded pending PDF (difficulty/ETA preflight). */
export async function classifyDirectUpload(uploadId: string): Promise<any | null> {
  try {
    const res = await fetch("/api/uploads/classify-pdf", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ uploadId }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}
